// pages/UserProfile/AccountConversion.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Link,
} from '@mui/material';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import axiosInstance from '../../../utils/axiosInstance';
import { useNavigate } from 'react-router-dom';
import { usePanel } from '../../../contexts/PanelContext';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textTertiary: '#94a3b8',
  accent: '#3b82f6',
  success: '#059669',
  error: '#dc2626',
  warning: '#f59e0b',
};

const AccountConversion = () => {
  const navigate = useNavigate();
  const { refreshPanelAccess } = usePanel();
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [paymentRequired, setPaymentRequired] = useState(false);

  useEffect(() => {
    fetchConversionPreview();
  }, []);

  const fetchConversionPreview = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get('/subscriptions/account/conversion-preview');
      setPreviewData(response.data);
      if (response.data.suggested_target_plan) {
        setSelectedPlanId(response.data.suggested_target_plan.id);
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to load conversion preview';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanChange = (event) => {
    setSelectedPlanId(event.target.value);
  };

  const getSelectedPlan = () => {
    if (!previewData?.available_target_plans) return null;
    return previewData.available_target_plans.find(p => p.id === selectedPlanId);
  };

  const calculateProration = () => {
    if (!previewData?.proration || !selectedPlanId) return null;
    const selectedPlan = getSelectedPlan();
    if (!selectedPlan) return previewData.proration;

    const { days_remaining, total_days, unused_credit } = previewData.proration;
    const fractionRemaining = days_remaining / total_days;
    const newPlanProrated = selectedPlan.price * fractionRemaining;
    const netCharge = Math.max(0, newPlanProrated - unused_credit);

    return {
      ...previewData.proration,
      new_plan_price: selectedPlan.price,
      new_plan_prorated: newPlanProrated.toFixed(2),
      net_charge: netCharge.toFixed(2),
    };
  };

  const handleConvertClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmConversion = async () => {
    setConfirmDialogOpen(false);
    setConverting(true);
    setError('');
    setSuccess('');

    try {
      const response = await axiosInstance.post('/subscriptions/account/convert', {
        target_account_type: previewData.target_account_type,
        target_plan_id: selectedPlanId,
      });

      setSuccess(response.data.message || 'Account converted successfully!');

      // Update localStorage with new account type so PanelContext picks it up
      const newAccountType = response.data.data?.account_type || previewData.target_account_type;
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        if (userData.active_team) {
          userData.active_team.team_account_type = newAccountType;
        }
        userData.account_type = newAccountType;
        localStorage.setItem('user', JSON.stringify(userData));
      }

      // Refresh panel access immediately so PanelSwitcher updates
      refreshPanelAccess();

      // Refresh page after successful conversion
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err) {
      const statusCode = err.response?.status;
      const message = err.response?.data?.message || 'Failed to convert account';

      if (statusCode === 402) {
        // Payment method required
        setPaymentRequired(true);
        setError('A payment method is required to complete this conversion. Please add a payment method first.');
      } else {
        setError(message);
      }
    } finally {
      setConverting(false);
    }
  };

  const handleAddPaymentMethod = () => {
    navigate('/Billing');
  };

  // Check if selected plan requires payment and user doesn't have payment method
  const selectedPlan = getSelectedPlan();
  const needsPaymentMethod = previewData?.requires_payment_method &&
                             !previewData?.has_payment_method &&
                             selectedPlan?.price > 0;

  const proration = calculateProration();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !previewData) {
    return (
      <Alert severity="error" sx={{ fontFamily, borderRadius: '12px' }}>
        {error}
      </Alert>
    );
  }

  if (!previewData) {
    return (
      <Alert severity="info" sx={{ fontFamily, borderRadius: '12px' }}>
        Unable to load conversion options.
      </Alert>
    );
  }

  const isBusinessAccount = previewData.current_account_type === 'business';
  const targetType = previewData.target_account_type;

  return (
    <Box>
      <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 600, color: colors.textPrimary, mb: 3 }}>
        Convert Account Type
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 3, fontFamily, borderRadius: '12px' }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, fontFamily, borderRadius: '12px' }}
          action={
            paymentRequired ? (
              <Button
                color="inherit"
                size="small"
                startIcon={<CreditCardIcon />}
                onClick={handleAddPaymentMethod}
                sx={{ fontFamily, textTransform: 'none' }}
              >
                Add Payment Method
              </Button>
            ) : null
          }
        >
          {error}
        </Alert>
      )}

      {/* Payment Method Warning */}
      {needsPaymentMethod && !error && (
        <Alert
          severity="warning"
          icon={<CreditCardIcon />}
          sx={{ mb: 3, fontFamily, borderRadius: '12px' }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleAddPaymentMethod}
              sx={{ fontFamily, textTransform: 'none' }}
            >
              Add Payment Method
            </Button>
          }
        >
          A payment method is required to convert to a paid plan. Please add a payment method to proceed.
        </Alert>
      )}

      {/* Current Account Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: '12px',
          border: '1px solid rgba(0,0,0,0.08)',
          bgcolor: 'rgba(59,130,246,0.03)',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            bgcolor: colors.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {isBusinessAccount ? (
              <BusinessIcon sx={{ color: '#fff', fontSize: 20 }} />
            ) : (
              <PersonOutlineIcon sx={{ color: '#fff', fontSize: 20 }} />
            )}
          </Box>
          <Box>
            <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Current Account
            </Typography>
            <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 600, color: colors.textPrimary }}>
              {isBusinessAccount ? 'Business' : 'Individual'} Account
            </Typography>
          </Box>
        </Box>

        {previewData.current_plan && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              label={`Plan: ${previewData.current_plan.display_name || previewData.current_plan.name}`}
              size="small"
              sx={{ fontFamily, bgcolor: 'rgba(59,130,246,0.1)', color: colors.accent }}
            />
            <Chip
              label={`$${previewData.current_plan.price}/${previewData.current_plan.interval || 'month'}`}
              size="small"
              sx={{ fontFamily, bgcolor: 'rgba(5,150,105,0.1)', color: colors.success }}
            />
            {previewData.current_subscription_status && (
              <Chip
                label={previewData.current_subscription_status === 'trialing' ? 'Trial' :
                       previewData.current_subscription_status === 'freemium' ? 'Free' :
                       previewData.current_subscription_status.charAt(0).toUpperCase() + previewData.current_subscription_status.slice(1)}
                size="small"
                sx={{
                  fontFamily,
                  bgcolor: previewData.current_subscription_status === 'trialing' ? 'rgba(245,158,11,0.1)' :
                           previewData.current_subscription_status === 'freemium' ? 'rgba(107,114,128,0.1)' :
                           'rgba(5,150,105,0.1)',
                  color: previewData.current_subscription_status === 'trialing' ? colors.warning :
                         previewData.current_subscription_status === 'freemium' ? colors.textSecondary :
                         colors.success
                }}
              />
            )}
            {previewData.proration && (
              <Chip
                label={`${previewData.proration.days_used} days used`}
                size="small"
                sx={{ fontFamily, bgcolor: 'rgba(0,0,0,0.06)', color: colors.textSecondary }}
              />
            )}
          </Box>
        )}
      </Paper>

      {/* Swap Icon */}
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
        <Box sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          bgcolor: 'rgba(59,130,246,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <SwapVertIcon sx={{ color: colors.accent, fontSize: 20 }} />
        </Box>
      </Box>

      {/* Target Account Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: '12px',
          border: '1px solid rgba(0,0,0,0.08)',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            bgcolor: isBusinessAccount ? colors.accent : '#8b5cf6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {isBusinessAccount ? (
              <PersonOutlineIcon sx={{ color: '#fff', fontSize: 20 }} />
            ) : (
              <BusinessIcon sx={{ color: '#fff', fontSize: 20 }} />
            )}
          </Box>
          <Box>
            <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Convert To
            </Typography>
            <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 600, color: colors.textPrimary }}>
              {targetType === 'business' ? 'Business' : 'Individual'} Account
            </Typography>
          </Box>
        </Box>

        {/* Benefits */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: colors.textSecondary, mb: 1 }}>
            {targetType === 'business' ? 'Business Benefits:' : 'Individual Features:'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {targetType === 'business' ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleOutlineIcon sx={{ color: colors.success, fontSize: 16 }} />
                  <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary }}>Operation Suit</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleOutlineIcon sx={{ color: colors.success, fontSize: 16 }} />
                  <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary }}>Scale & Growth</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleOutlineIcon sx={{ color: colors.success, fontSize: 16 }} />
                  <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary }}>Customer Seprate Panel</Typography>
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleOutlineIcon sx={{ color: colors.success, fontSize: 16 }} />
                  <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary }}>Personal workspace</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleOutlineIcon sx={{ color: colors.success, fontSize: 16 }} />
                  <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary }}>Lower pricing</Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Team Warning for Business -> Individual */}
        {previewData.team_member_count > 0 && (
          <Alert
            severity="warning"
            icon={<WarningAmberIcon />}
            sx={{ mb: 3, fontFamily, borderRadius: '10px' }}
          >
            You have {previewData.team_member_count} team member(s). Converting to Individual will remove their access.
          </Alert>
        )}

        {/* Plan Selection */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: colors.textSecondary, mb: 1 }}>
            Select Target Plan
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={selectedPlanId}
              onChange={handlePlanChange}
              sx={{
                fontFamily,
                borderRadius: '10px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.1)' },
              }}
            >
              {previewData.available_target_plans?.map((plan) => (
                <MenuItem key={plan.id} value={plan.id} sx={{ fontFamily }}>
                  {plan.display_name || plan.name} - ${plan.price}/{plan.interval || 'month'}
                  {previewData.suggested_target_plan?.id === plan.id && ' (Suggested)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Cost Breakdown */}
      {proration && selectedPlanId && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: '12px',
            border: '1px solid rgba(0,0,0,0.08)',
            bgcolor: 'rgba(0,0,0,0.02)',
            mb: 3,
          }}
        >
          <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: colors.textPrimary, mb: 2 }}>
            Cost Breakdown
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary }}>
              Credit for {proration.days_remaining} unused days
            </Typography>
            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.success, fontWeight: 500 }}>
              -${proration.unused_credit}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary }}>
              New plan ({proration.days_remaining} days prorated)
            </Typography>
            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary }}>
              ${proration.new_plan_prorated}
            </Typography>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontFamily, fontSize: '0.95rem', fontWeight: 600, color: colors.textPrimary }}>
              Amount to charge
            </Typography>
            <Typography sx={{ fontFamily, fontSize: '0.95rem', fontWeight: 700, color: colors.accent }}>
              ${proration.net_charge}
            </Typography>
          </Box>

          <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary, mt: 1.5 }}>
            Your billing date remains unchanged. You'll be charged the full new plan price on your next billing date.
          </Typography>
        </Paper>
      )}

      {/* Convert Button */}
      {needsPaymentMethod ? (
        <Button
          variant="contained"
          fullWidth
          onClick={handleAddPaymentMethod}
          startIcon={<CreditCardIcon />}
          sx={{
            fontFamily,
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '12px',
            py: 1.5,
            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
            boxShadow: '0 4px 16px rgba(245,158,11,0.4)',
            '&:hover': {
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          Add Payment Method to Continue
        </Button>
      ) : (
        <Button
          variant="contained"
          fullWidth
          onClick={handleConvertClick}
          disabled={converting || !selectedPlanId}
          startIcon={converting ? <CircularProgress size={16} color="inherit" /> : <SwapVertIcon />}
          sx={{
            fontFamily,
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '12px',
            py: 1.5,
            background: isBusinessAccount
              ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)'
              : 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
            boxShadow: isBusinessAccount
              ? '0 4px 16px rgba(59,130,246,0.4)'
              : '0 4px 16px rgba(139,92,246,0.4)',
            '&:hover': {
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          {converting ? 'Converting...' : `Convert to ${targetType === 'business' ? 'Business' : 'Individual'} Account`}
        </Button>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
      >
        <DialogTitle sx={{ fontFamily, fontWeight: 600 }}>
          Confirm Account Conversion
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily }}>
            You are about to convert your account from{' '}
            <strong>{isBusinessAccount ? 'Business' : 'Individual'}</strong> to{' '}
            <strong>{targetType === 'business' ? 'Business' : 'Individual'}</strong>.
            {proration && (
              <>
                <br /><br />
                You will be charged <strong>${proration.net_charge}</strong> for the prorated difference.
              </>
            )}
            {previewData.team_member_count > 0 && (
              <>
                <br /><br />
                <span style={{ color: colors.warning }}>
                  Warning: Your {previewData.team_member_count} team member(s) will lose access.
                </span>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            sx={{ fontFamily, textTransform: 'none', borderRadius: '10px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmConversion}
            variant="contained"
            sx={{
              fontFamily,
              textTransform: 'none',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
            }}
          >
            Confirm Conversion
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountConversion;
